import { useNavigate, createFileRoute } from '@tanstack/react-router'
import RazorpayCheckout, { type BookingDetails } from '@/components/custom/ui/clients/tempula/payment/razorpay'
import type { PaymentSuccessData } from '@/hooks/useRazorpay'
import type { ReceiptData } from '@/components/custom/ui/clients/tempula/payment/receipt'
import { tempulaPaymentConfig, tempulaPaymentService } from '@/constant/config/clients/tempula/payment/paymentConfig'
import { TEMPULA_ROOMS } from '@/constant/config/clients/tempula/rooms/roomCatalogue'
import { useBookingStore } from '@/stores/bookingStore'
import { format } from 'date-fns'

export const Route = createFileRoute('/_clients/tempula/payment')({
  component: RouteComponent,
})

function useBookingDetails(): BookingDetails | null {
  const { rooms, dateRange, extras, guestDetails } = useBookingStore()

  if (!dateRange?.from || !dateRange?.to) return null

  const dateFrom = new Date(dateRange.from)
  const dateTo   = new Date(dateRange.to)
  const nights   = Math.max(1, Math.ceil((dateTo.getTime() - dateFrom.getTime()) / (1000 * 60 * 60 * 24)))

  const adults      = extras.adults
  const children    = extras.children
  const infants     = extras.infants
  const petsAllowed = extras.petsAllowed
  const foodAllowed = extras.foodAllowed

  const activeRooms = Object.entries(rooms)
    .filter(([, r]) => r.quantity > 0)
    .map(([id, r]) => {
      const room = TEMPULA_ROOMS[id]
      if (!room) return null
      return { id, name: room.fullName, pricePerNight: room.pricePerNight, qty: r.quantity, freeAdults: room.capacity }
    })
    .filter(Boolean) as { id: string; name: string; pricePerNight: number; qty: number; freeAdults: number }[]

  if (activeRooms.length === 0) return null

  const primaryRoom   = activeRooms[0]
  const extraAdults   = adults > 0   ? Math.max(0, adults - primaryRoom.freeAdults) : 0
  const extraChildren = children > 0 ? Math.max(0, children - 1)                    : 0
  const foodPeople    = adults + children || activeRooms.reduce((s, r) => s + r.qty, 0)

  const roomsCost         = activeRooms.reduce((s, r) => s + r.pricePerNight * r.qty * nights, 0)
  const extraAdultCost    = extraAdults   * 0 * nights
  const extraChildrenCost = extraChildren * 0  * nights
  const foodCost          = foodAllowed ? foodPeople * 1000 * nights : 0
  const amount            = roomsCost + extraAdultCost + extraChildrenCost + foodCost

  const { title, firstName, lastName, email, phone, address, state, country, pincode } = guestDetails

  return {
    roomIds:        activeRooms.map(r => r.id),
    roomName:       activeRooms.map(r => `${r.qty > 1 ? `${r.qty}× ` : ""}${r.name}`).join(", "),
    name:           `${firstName} ${lastName}`.trim() || "Guest",
    guestTitle:     title,
    guestFirstName: firstName,
    guestLastName:  lastName,
    email,
    phone,
    guestAddress:   address,
    guestState:     state,
    guestCountry:   country,
    guestPincode:   pincode,
    checkIn:        format(dateFrom, "yyyy-MM-dd"),
    checkOut:       format(dateTo,   "yyyy-MM-dd"),
    nights,
    adults,
    children,
    infants,
    petsAllowed,
    petsCount:      petsAllowed ? 1 : 0,
    foodAllowed,
    rooms: activeRooms.map(r => ({
      name:          r.name,
      qty:           r.qty,
      pricePerNight: r.pricePerNight,
      subtotal:      r.pricePerNight * r.qty * nights,
    })),
    extraAdultCost,
    extraChildrenCost,
    foodCost,
    foodPeople,
    amount,
  }
}

function RouteComponent() {
  const navigate = useNavigate()
  const booking  = useBookingDetails()
  const { resetBooking } = useBookingStore()

  if (!booking) {
    return (
      <div className="min-h-screen bg-BgGallery flex items-center justify-center">
        <div className="bg-myNav border border-white/10 rounded-2xl p-10 text-center max-w-sm">
          <p className="text-white/60 text-sm mb-4">No booking data found.</p>
          <button
            onClick={() => navigate({ to: "/tempula" })}
            className="bg-myBlue hover:bg-myBlueHover text-white px-6 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const handleSuccess = (payment: PaymentSuccessData) => {
    const guestCount = (booking.adults + booking.children + booking.infants) || 1

    const receipt: ReceiptData = {
      bookingId:         payment.bookingId,
      razorpayPaymentId: payment.razorpayPaymentId,
      razorpayOrderId:   payment.razorpayOrderId,
      totalPrice:        payment.totalPrice || booking.amount,
      currency:          payment.currency   || "INR",
      guestName:         booking.name,
      guestEmail:        booking.email,
      guestMobile:       booking.phone,
      roomName:          booking.roomName,
      checkIn:           booking.checkIn,
      checkOut:          booking.checkOut,
      guests:            guestCount,
      paidAt:            new Date().toISOString(),
    }
    sessionStorage.setItem("receipt", JSON.stringify(receipt))
    resetBooking()
    navigate({ to: "/tempula/receipt" })
  }

  return (
    <RazorpayCheckout
      booking={booking}
      config={tempulaPaymentConfig}
      service={tempulaPaymentService}
      onSuccess={handleSuccess}
    />
  )
}
